import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileText, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

function formatBytes(b) {
    if (!b && b !== 0) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

export function FileUploadField({ field, submissionId, currentUpload, onChange, disabled }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const triggerPick = () => inputRef.current?.click();

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // reset for re-selection
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("File exceeds 10 MB");
            return;
        }
        const fd = new FormData();
        fd.append("field_key", field.key);
        fd.append("file", file);
        setUploading(true);
        try {
            const { data } = await api.post(`/submissions/${submissionId}/uploads`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data);
            toast.success(`${field.label} uploaded`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    const onRemove = async () => {
        if (!currentUpload) return;
        setUploading(true);
        try {
            await api.delete(`/submissions/${submissionId}/uploads/${currentUpload.id}`);
            onChange(null);
            toast.success(`${field.label} removed`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    return (
        <div data-testid={`upload-${field.key}`} className="rounded-md border border-slate-200 bg-white p-4">
            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={onFile}
                data-testid={`upload-${field.key}-input`}
                disabled={disabled || uploading}
            />
            {currentUpload ? (
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-blue-900 shrink-0">
                            <FileText size={18} />
                        </span>
                        <div className="min-w-0">
                            <div className="font-medium text-sm text-slate-900 truncate">{currentUpload.filename}</div>
                            <div className="text-xs text-slate-500">
                                {formatBytes(currentUpload.size)} · {currentUpload.mime_type}
                            </div>
                        </div>
                    </div>
                    {!disabled && (
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={triggerPick}
                                disabled={uploading}
                                data-testid={`upload-${field.key}-replace`}
                            >
                                Replace
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={onRemove}
                                disabled={uploading}
                                data-testid={`upload-${field.key}-remove`}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                                <X size={14} />
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                <button
                    type="button"
                    onClick={triggerPick}
                    disabled={disabled || uploading}
                    data-testid={`upload-${field.key}-trigger`}
                    className="w-full border-2 border-dashed border-slate-200 hover:border-blue-300 hover:bg-blue-50/40 transition-colors rounded-md py-6 px-4 text-center disabled:opacity-50"
                >
                    {uploading ? (
                        <span className="inline-flex items-center gap-2 text-sm text-slate-600">
                            <Loader2 size={16} className="animate-spin" /> Uploading…
                        </span>
                    ) : (
                        <>
                            <UploadCloud className="mx-auto text-slate-400 mb-2" size={22} />
                            <div className="font-medium text-sm text-slate-900">Click to upload</div>
                            <div className="text-xs text-slate-500 mt-0.5">PDF, JPG, or PNG · up to 10 MB</div>
                        </>
                    )}
                </button>
            )}
        </div>
    );
}
